import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import * as L from 'leaflet';
import { GeoJsonObject } from 'geojson';
import { HOUSEKEEPING_COMMAND_CENTRE_DATA } from './house-keeping-command-centre.data';

interface CampusSource {
  id: number;
  lat: number;
  lon: number;
  st: string;
  c: string;
}

interface CampusRecord extends CampusSource {
  staff: number;
  done: boolean;
  mk: L.CircleMarker;
}

const STATE_FULL: Record<string, string> = {
  AN: 'Andaman & Nicobar',
  AP: 'Andhra Pradesh',
  AR: 'Arunachal Pradesh',
  AS: 'Assam',
  BR: 'Bihar',
  CH: 'Chandigarh',
  CG: 'Chhattisgarh',
  DN: 'Dadra & Nagar Haveli',
  DD: 'Daman & Diu',
  DL: 'Delhi',
  GA: 'Goa',
  GJ: 'Gujarat',
  HR: 'Haryana',
  HP: 'Himachal Pradesh',
  JK: 'Jammu & Kashmir',
  JH: 'Jharkhand',
  KA: 'Karnataka',
  KL: 'Kerala',
  LD: 'Lakshadweep',
  MP: 'Madhya Pradesh',
  MH: 'Maharashtra',
  MN: 'Manipur',
  ML: 'Meghalaya',
  MZ: 'Mizoram',
  NL: 'Nagaland',
  OR: 'Odisha',
  PY: 'Puducherry',
  PB: 'Punjab',
  RJ: 'Rajasthan',
  SK: 'Sikkim',
  TN: 'Tamil Nadu',
  TR: 'Tripura',
  UP: 'Uttar Pradesh',
  UK: 'Uttarakhand',
  WB: 'West Bengal',
};

const FIRST_NAMES = [
  'Aarav', 'Vikram', 'Priya', 'Rohan', 'Sunita', 'Anil', 'Deepa', 'Karthik', 'Meena', 'Suresh',
  'Anjali', 'Ravi', 'Pooja', 'Manoj', 'Lakshmi', 'Imran', 'Neha', 'Arjun', 'Kavya', 'Sanjay',
];
const LAST_NAMES = [
  'Sharma', 'Patel', 'Reddy', 'Iyer', 'Singh', 'Das', 'Nair', 'Kumar', 'Gupta', 'Mehta',
  'Rao', 'Khan', 'Joshi', 'Verma', 'Pillai',
];
const ROLES = ['Housekeeping Lead', 'Sanitation Staff', 'Shift Supervisor', 'Facility Attendant'];
const AVATAR_COLORS = ['#46c8f5', '#1fd17f', '#f5bd45', '#9485f2', '#ffb547', '#ff8a8a'];
const CITY_BY_STATE: Record<string, string[]> = {
  MH: ['Mumbai', 'Pune', 'Nagpur', 'Nashik'],
  KA: ['Bengaluru', 'Mysuru', 'Hubballi'],
  TN: ['Chennai', 'Coimbatore', 'Madurai'],
  UP: ['Lucknow', 'Noida', 'Kanpur', 'Varanasi'],
  DL: ['New Delhi', 'Dwarka'],
  GJ: ['Ahmedabad', 'Surat', 'Vadodara'],
  RJ: ['Jaipur', 'Jodhpur', 'Udaipur'],
  WB: ['Kolkata', 'Howrah', 'Siliguri'],
  AP: ['Visakhapatnam', 'Vijayawada'],
  KL: ['Kochi', 'Thiruvananthapuram'],
  MP: ['Indore', 'Bhopal'],
  PB: ['Ludhiana', 'Mohali'],
  HR: ['Gurugram', 'Faridabad'],
  BR: ['Patna'],
  OR: ['Bhubaneswar'],
  AS: ['Guwahati'],
  JH: ['Ranchi'],
  CG: ['Raipur'],
};
const NEWS = [
  'New cleaning SOP v3.2 rolled out to all regions',
  'Monsoon protocol active — wet-floor signage mandatory in 6 states',
  'Quarterly deep-clean audit begins Monday',
  'Supply restock confirmed for West & South zones',
  'Photo-proof upload now required before shift sign-off',
];

@Component({
  selector: 'app-house-keeping-command-centre',
  templateUrl: './house-keeping-command-centre.component.html',
  styleUrls: ['./house-keeping-command-centre.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HouseKeepingCommandCentreComponent implements OnInit, AfterViewInit, OnDestroy {
  private map: L.Map | null = null;
  private campuses: CampusRecord[] = [];
  private total = 0;
  private totalEmp = 0;
  private doneCount = 0;
  private empIn = 0;
  private hourStamps: number[] = [];
  private reportTimes: number[] = [];
  private statesSeen = new Set<string>();
  private liveTimer: ReturnType<typeof setTimeout> | null = null;
  private clockTimer: ReturnType<typeof setInterval> | null = null;
  private reportLoopTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly resizeHandler = () => this.refreshMapSize();
  private readonly data = HOUSEKEEPING_COMMAND_CENTRE_DATA as unknown as {
    campuses: CampusSource[];
    states: GeoJsonObject;
    outline: GeoJsonObject;
  };

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    document.body.classList.add('hkcc-body-lock');
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.resizeHandler);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.initMap();
        this.seed(0.34);
        this.initTickerNews();
        this.boot();
        this.refreshMapSize();
        this.clockTimer = setInterval(() => this.tickClock(), 1000);
        this.tickClock();
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scheduleReportLoop(reduce);
      });
    });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('hkcc-body-lock');
    window.removeEventListener('resize', this.resizeHandler);
    if (this.clockTimer) clearInterval(this.clockTimer);
    if (this.liveTimer) clearTimeout(this.liveTimer);
    if (this.reportLoopTimer) clearTimeout(this.reportLoopTimer);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private refreshMapSize(): void {
    if (!this.map) return;
    this.map.invalidateSize({ animate: false, pan: false });
    const center = this.map.getCenter();
    const zoom = this.map.getZoom();
    this.map.setView(center, zoom, { animate: false });
  }

  private pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  private istNow(): Date {
    return new Date(Date.now() + (330 + new Date().getTimezoneOffset()) * 60000);
  }

  private hhmm(d: Date): string {
    return `${this.pad(d.getHours())}:${this.pad(d.getMinutes())}`;
  }

  private tickClock(): void {
    const t = this.istNow();
    const clock = this.$('clock');
    const date = this.$('date');
    if (clock) {
      clock.innerHTML = `${this.pad(t.getHours())}:${this.pad(t.getMinutes())}:${this.pad(t.getSeconds())}<span>IST</span>`;
    }
    if (date) {
      date.textContent = t
        .toLocaleDateString('en-IN', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
        .toUpperCase();
    }
  }

  private initMap(): void {
    const mapEl = this.$('map');
    if (!mapEl) return;

    this.map = L.map(mapEl, {
      preferCanvas: true,
      zoomControl: true,
      minZoom: 4,
      maxZoom: 9,
      maxBounds: [
        [5, 66],
        [38, 99],
      ],
      maxBoundsViscosity: 0.9,
      attributionControl: true,
    }).setView([22.6, 81], 5);

    const dark = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      subdomains: 'abcd',
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap &copy; CARTO',
    });
    const sat = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      { maxZoom: 18, attribution: 'Imagery &copy; Esri' }
    );
    dark.addTo(this.map);
    L.control
      .layers({ 'Dark command': dark, Satellite: sat }, undefined, { position: 'topright' })
      .addTo(this.map);

    const svgR = L.svg();
    const stateLayerOptions = {
      renderer: svgR,
      style: {
        color: '#3a6ea5',
        weight: 0.7,
        opacity: 0.45,
        fill: true,
        fillColor: '#0e2a47',
        fillOpacity: 0.28,
      },
    } as L.GeoJSONOptions;
    L.geoJSON(this.data.states, stateLayerOptions).addTo(this.map);

    const outlineLayerOptions = {
      renderer: svgR,
      style: { color: '#46c8f5', weight: 1.8, opacity: 0.9, fill: false },
    } as L.GeoJSONOptions;
    L.geoJSON(this.data.outline, outlineLayerOptions).addTo(this.map);

    const canvasR = L.canvas({ padding: 0.5 });
    this.campuses = this.data.campuses.map((c) => {
      const staff = 2 + Math.floor(Math.random() * 4);
      const mk = L.circleMarker([c.lat, c.lon], {
        renderer: canvasR,
        radius: 3.6,
        stroke: false,
        fillColor: '#ff4646',
        fillOpacity: 0.92,
      });
      mk.addTo(this.map!);
      return { ...c, staff, done: false, mk };
    });

    this.total = this.campuses.length;
    this.totalEmp = this.campuses.reduce((sum, c) => sum + c.staff, 0);
    setTimeout(() => {
      this.refreshMapSize();
    }, 100);
  }

  private ripple(lat: number, lon: number): void {
    if (!this.map) return;
    const svgR = L.svg();
    const r = L.circleMarker([lat, lon], {
      renderer: svgR,
      radius: 5,
      color: '#1fd17f',
      weight: 2.5,
      fill: false,
      className: 'rip',
    }).addTo(this.map);
    let k = 0;
    const id = setInterval(() => {
      k += 0.07;
      r.setRadius(5 + k * 20);
      r.setStyle({ opacity: Math.max(0, 0.9 * (1 - k)) });
      if (k >= 1) {
        clearInterval(id);
        this.map?.removeLayer(r);
      }
    }, 16);
  }

  private seed(frac: number): void {
    const idx = [...this.campuses.keys()];
    for (let i = idx.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [idx[i], idx[j]] = [idx[j], idx[i]];
    }
    const target = Math.floor(this.total * frac);
    for (let k = 0; k < target; k++) {
      const c = this.campuses[idx[k]];
      c.done = true;
      c.mk.setStyle({ fillColor: '#1fd17f' });
      this.doneCount++;
      this.empIn += c.staff;
      this.statesSeen.add(c.st);
    }
  }

  private photo(c: CampusRecord): HTMLDivElement {
    const hue = (c.id * 47) % 360;
    const wrap = document.createElement('div');
    wrap.style.position = 'absolute';
    wrap.style.inset = '0';
    const bg = document.createElement('div');
    bg.className = 'ph';
    bg.style.background = `linear-gradient(150deg, hsl(${hue} 40% 26%), hsl(${(hue + 40) % 360} 45% 16%))`;
    const room = document.createElement('div');
    room.className = 'room';
    room.innerHTML = `<svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" style="position:absolute;inset:0;width:100%;height:100%;opacity:.9"><g stroke="hsl(${hue} 50% 70% / .5)" stroke-width=".8" fill="none"><path d="M0 64 L100 64"/><path d="M0 64 L24 40 L76 40 L100 64"/><rect x="24" y="40" width="52" height="24"/><path d="M50 40 L50 64"/><rect x="30" y="68" width="18" height="14" fill="hsl(${hue} 45% 40% / .5)"/><rect x="54" y="68" width="18" height="10" fill="hsl(${hue} 45% 36% / .5)"/><circle cx="84" cy="30" r="6" fill="hsl(48 90% 70% / .5)" stroke="none"/></g></svg>`;
    wrap.appendChild(bg);
    wrap.appendChild(room);
    return wrap;
  }

  private person(c: CampusRecord): { name: string; role: string; initials: string; color: string } {
    const fn = FIRST_NAMES[(c.id * 7) % FIRST_NAMES.length];
    const ln = LAST_NAMES[(c.id * 13) % LAST_NAMES.length];
    return {
      name: `${fn} ${ln}`,
      role: ROLES[c.id % ROLES.length],
      initials: `${fn[0]}${ln[0]}`,
      color: AVATAR_COLORS[c.id % AVATAR_COLORS.length],
    };
  }

  private siteName(c: CampusRecord): string {
    const cities = CITY_BY_STATE[c.st];
    const city = cities ? cities[c.id % cities.length] : STATE_FULL[c.st];
    const kinds = ['Tech Park', 'Corporate Campus', 'Logistics Hub', 'Office Tower', 'Manufacturing Unit', 'Data Centre'];
    return `${city} ${kinds[c.id % kinds.length]}`;
  }

  private showLive(c: CampusRecord): void {
    const p = this.person(c);
    const t = this.istNow();
    const slot = this.$('liveSlot');
    if (!slot) return;

    slot.className = 'livecard flash';
    slot.innerHTML = '';
    const ph = document.createElement('div');
    ph.className = 'photo';
    ph.appendChild(this.photo(c));
    ph.insertAdjacentHTML(
      'beforeend',
      `<div class="verified"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>Verified</div><div class="geo"><span class="pin"></span>${this.siteName(c)} · ${STATE_FULL[c.st]}</div>`
    );
    const info = document.createElement('div');
    info.className = 'liveinfo';
    info.innerHTML = `<div class="eyebrow"><span class="pulse"></span>New upload · housekeeping complete</div><div class="code">${c.c}</div><div class="site">${this.siteName(c)}</div><div class="state">${STATE_FULL[c.st]}</div><div class="spacer"></div><div class="who"><div class="av" style="background:${p.color}">${p.initials}</div><div class="nm"><b>${p.name}</b><span>${p.role}</span></div><div class="ts"><b class="tnum">${this.hhmm(t)}</b><span>logged in</span></div></div>`;
    slot.appendChild(ph);
    slot.appendChild(info);
    if (this.liveTimer) clearTimeout(this.liveTimer);
    this.liveTimer = setTimeout(() => this.tuckToRecent(c), 5200);
  }

  private tuckToRecent(c: CampusRecord): void {
    const grid = this.$('recentGrid');
    if (!grid) return;

    const th = document.createElement('div');
    th.className = 'thumb';
    th.appendChild(this.photo(c));
    th.insertAdjacentHTML(
      'beforeend',
      `<div class="chk"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg></div><div class="cap"><b>${c.c}</b><span class="tnum">${this.hhmm(this.istNow())}</span></div>`
    );
    grid.insertBefore(th, grid.firstChild);
    while (grid.children.length > 10) {
      grid.removeChild(grid.lastChild!);
    }

    const slot = this.$('liveSlot');
    if (!slot) return;
    slot.className = 'idlecard';
    slot.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg><span>Waiting for next upload…</span>';
  }

  private animateNum(el: HTMLElement, to: number, dur = 900): void {
    const from = parseInt(el.textContent?.replace(/[^0-9]/g, '') ?? '0', 10) || 0;
    if (from === to) {
      el.textContent = this.fmt(to);
      return;
    }
    const start = performance.now();
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / dur);
      const e = 1 - Math.pow(1 - k, 3);
      el.textContent = this.fmt(from + (to - from) * e);
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  private flashTrend(id: string): void {
    const el = this.$(id);
    if (!el) return;
    el.classList.remove('show');
    void el.offsetWidth;
    el.classList.add('show');
  }

  private refresh(animate: boolean): void {
    const pend = this.total - this.doneCount;
    const pct = Math.round((this.doneCount / this.total) * 100);
    const mClean = this.$('mClean');
    const mIn = this.$('mIn');

    if (animate) {
      if (mClean) this.animateNum(mClean, this.doneCount);
      if (mIn) this.animateNum(mIn, this.empIn);
    } else {
      if (mClean) mClean.textContent = this.fmt(this.doneCount);
      if (mIn) mIn.textContent = this.fmt(this.empIn);
    }

    this.setText('mTotal', this.fmt(this.total));
    this.setText('mPend', this.fmt(pend));
    this.setText('mEmp', this.fmt(this.totalEmp));
    this.setText('mStaffPend', this.fmt(this.totalEmp - this.empIn));
    this.setText('mCleanPct', `${pct}%`);
    this.setText('mPendPct', `${100 - pct}%`);
    this.setText('mInPct', `${Math.round((this.empIn / this.totalEmp) * 100)}%`);

    const ring = this.$('ring');
    if (ring) ring.setAttribute('stroke-dashoffset', String(515.2 * (1 - this.doneCount / this.total)));
    this.setText('pct', String(pct));
    this.setText('doneN', this.fmt(this.doneCount));
    this.setText('totN', this.fmt(this.total));
    this.setText('mapDone', this.fmt(this.doneCount));
    this.setText('mapPend', this.fmt(pend));
    this.setText('stReport', `${this.statesSeen.size} / 35`);
    this.setText('recentCt', `${this.fmt(this.doneCount)} today`);

    const cutoff = Date.now() - 3600000;
    this.hourStamps = this.hourStamps.filter((stamp) => stamp > cutoff);
    this.setText('lastHr', this.fmt(this.hourStamps.length));
  }

  private initTickerNews(): void {
    const m = this.$('tickMove');
    if (!m) return;
    NEWS.forEach((n) => {
      const s = document.createElement('span');
      s.className = 'ev';
      s.innerHTML = `<b>UPDATE</b> · ${n}`;
      m.appendChild(s);
    });
  }

  private pushTick(c: CampusRecord): void {
    const m = this.$('tickMove');
    if (!m) return;
    const ev = document.createElement('span');
    ev.className = 'ev';
    ev.innerHTML = `<span class="ok">✓ ${c.c}</span> ${this.siteName(c)} reported in · <b>${this.hhmm(this.istNow())}</b>`;
    m.insertBefore(ev, m.firstChild);
    while (m.children.length > 16) {
      m.removeChild(m.lastChild!);
    }
  }

  private reportIn(): void {
    const pend = this.campuses.filter((c) => !c.done);
    if (!pend.length) {
      this.refresh(false);
      return;
    }
    const c = pend[Math.floor(Math.random() * pend.length)];
    c.done = true;
    c.mk.setStyle({ fillColor: '#1fd17f' });
    this.ripple(c.lat, c.lon);
    this.doneCount++;
    this.empIn += c.staff;
    this.statesSeen.add(c.st);
    this.hourStamps.push(Date.now());
    this.reportTimes.push(2 + Math.random() * 7);
    const avg =
      this.reportTimes.slice(-50).reduce((a, b) => a + b, 0) /
      Math.min(this.reportTimes.length, 50);
    this.setText('avgT', `${avg.toFixed(1)} min`);
    this.setText('lastUpd', this.hhmm(this.istNow()));
    this.showLive(c);
    this.pushTick(c);
    this.refresh(true);
    this.flashTrend('tClean');
    this.flashTrend('tIn');
    this.flashTrend('tPend');
  }

  private boot(): void {
    this.setText('mTotal', '0');
    const mTotal = this.$('mTotal');
    if (mTotal) this.animateNum(mTotal, this.total, 1100);
    this.refresh(true);
    this.setText('avgT', '4.6 min');
    this.setText('lastUpd', this.hhmm(this.istNow()));
  }

  private scheduleReportLoop(reducedMotion: boolean): void {
    const loop = () => {
      this.reportIn();
      const delay = reducedMotion ? 4000 : 2200 + Math.random() * 2200;
      this.reportLoopTimer = setTimeout(loop, delay);
    };
    loop();
  }

  private $(id: string): HTMLElement | null {
    return this.host.nativeElement.querySelector(`#${id}`);
  }

  private setText(id: string, value: string): void {
    const el = this.$(id);
    if (el) el.textContent = value;
  }

  private fmt(n: number): string {
    return Math.round(n).toLocaleString('en-IN');
  }
}
